/**
 * Iter 86d — TOTP 2FA enrolment wizard (mandatory on first login).
 *
 * Hands the user the QR code + manual base32 key. Polls them for the
 * 6-digit code from their authenticator app, posts it to
 * `/api/mfa/totp/verify` and on success refreshes AuthContext so the
 * app un-gates. Backend also drops a 7-day trusted-device cookie at
 * this point so the user isn't prompted again on this browser.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ShieldCheck, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TotpEnrolWizard() {
  const { user, checkAuth } = useAuth();
  const [bundle, setBundle] = useState(null); // { secret, qr_png_b64, uri }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/mfa/totp/setup");
        if (!cancelled) setBundle(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to start enrolment");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/mfa/totp/verify", { code });
      toast.success("Two-factor authentication enabled.");
      await checkAuth();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    if (!bundle?.secret) return;
    try {
      await navigator.clipboard.writeText(bundle.secret);
      toast.success("Secret copied — paste it into your authenticator app.");
    } catch (_e) {
      toast.error("Couldn't copy.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="totp-enrol-wizard">
      <Card className="w-full max-w-md p-7 space-y-5 bg-white">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-violet-700">
            <ShieldCheck className="h-5 w-5" />
            <div className="text-xs uppercase tracking-[0.18em] font-semibold">Two-factor required</div>
          </div>
          <h1 className="font-display text-2xl tracking-tight text-slate-900">Set up Google Authenticator</h1>
          <p className="text-sm text-slate-600 leading-relaxed">
            Welcome <span className="font-medium">{user?.name?.split(" ")[0]}</span>. Before continuing, every Close user needs a one-time
            authenticator code. Scan the QR with <b>Google Authenticator</b> or any TOTP app, then enter the 6-digit code below.
          </p>
        </div>

        {!bundle ? (
          <div className="text-sm text-slate-500 flex items-center gap-2 py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Generating your secret…
          </div>
        ) : (
          <>
            <div className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-md p-4">
              <img
                src={`data:image/png;base64,${bundle.qr_png_b64}`}
                alt="TOTP QR"
                className="w-32 h-32 rounded-sm bg-white border border-slate-200"
                data-testid="totp-qr"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  Or enter manually
                </div>
                <div className="flex items-center gap-2">
                  <code
                    className="font-mono text-[12px] text-slate-800 bg-white border border-slate-200 rounded px-2 py-1 break-all flex-1"
                    data-testid="totp-secret"
                  >
                    {bundle.secret}
                  </code>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="text-slate-500 hover:text-slate-800 p-1"
                    title="Copy secret"
                    data-testid="totp-copy-secret"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="text-[11px] text-slate-500 leading-relaxed">
                  Account: <span className="font-mono">{user?.email}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Enter the 6-digit code</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123 456"
                className="text-center font-mono text-lg tracking-[0.4em] h-11"
                data-testid="totp-code-input"
                autoFocus
              />
              {error && (
                <div className="text-xs text-red-600 mt-1.5" data-testid="totp-error">{error}</div>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={busy || code.length !== 6}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="totp-verify-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify &amp; finish
            </Button>

            <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
              This device will be remembered for 7 days. If you lose access to your authenticator,
              ask your firm admin to reset it from Admin → Team.
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
